import { keepPreviousData, skipToken, type UseQueryOptions, useQuery } from '@tanstack/react-query';
import type {
  ResourceFilterStatus,
  ResourceStatus,
  SearchMembershipsSearchParams,
  TeamWithIncludedResponse,
} from '@/common/api/types/teams';
import { TeamsApi } from '@/features/teams/api/team';
import { teamsQueryKeys } from '@/features/teams/queryKeys';

export const getTeamQueryOptions = ({
  teamId,
  resourceStatus,
}: {
  teamId: number;
  resourceStatus?: ResourceFilterStatus;
}) => {
  return {
    queryKey: teamsQueryKeys.team({ teamId, resourceStatus }),
    queryFn: () => TeamsApi.getTeam(teamId, resourceStatus),
    enabled: !!teamId,
    staleTime: 60 * 60 * 1000,
  };
};

export const useGetTeamQuery = ({
  teamId,
  resourceStatus,
  options = {},
}: {
  teamId?: number | null;
  resourceStatus?: ResourceStatus;
  options?: Partial<UseQueryOptions<TeamWithIncludedResponse, Error, TeamWithIncludedResponse>>;
}) => {
  return useQuery({
    queryKey: teamsQueryKeys.team({ teamId, resourceStatus }),
    queryFn: teamId ? () => TeamsApi.getTeam(teamId, resourceStatus) : skipToken,
    ...options,
  });
};

export const getSearchMembershipsByTeamQueryOptions = ({
  teamId,
  searchParams,
}: {
  teamId: number;
  searchParams?: SearchMembershipsSearchParams;
}) => {
  return {
    queryKey: teamsQueryKeys.searchTeamMembers({ teamId, searchParams }),
    queryFn: () => TeamsApi.searchTeamMembershipsByTeam(teamId, searchParams),
    enabled: !!teamId,
  };
};

export const useSearchMembershipsByTeamQuery = ({
  teamId,
  searchParams,
}: {
  teamId: number;
  searchParams?: SearchMembershipsSearchParams;
}) => {
  return useQuery<TeamWithIncludedResponse>({
    ...getSearchMembershipsByTeamQueryOptions({
      teamId,
      searchParams,
    }),
    placeholderData: keepPreviousData,
  });
};
